// Floating - shared positioning helper over @floating-ui/dom (successor of
// popper.js/tippy.js). Positions a floating element (tooltip, dropdown menu,
// popover, feature highlight) relative to a reference element and keeps it
// anchored on scroll/resize via autoUpdate.

import {
  arrow,
  autoUpdate,
  computePosition,
  flip,
  offset,
  shift,
  type Placement,
  type Side,
  type Strategy,
} from '@floating-ui/dom';

export type { Placement, Strategy };

export interface FloatingOptions {
  placement?: Placement;
  /** Distance between reference and floating element, px. */
  offset?: number;
  /** Arrow element inside the floating element; positioned by the arrow middleware. */
  arrowElement?: HTMLElement | null;
  /** Viewport padding for shift(), px. */
  padding?: number;
  /**
   * CSS positioning scheme. 'absolute' (default) keeps the element in its
   * containing block - matching what plain CSS did. 'fixed' escapes ancestor
   * `overflow: hidden` clipping without moving the element in the DOM; the
   * element must then be `position: fixed` in CSS as well (set inline here).
   */
  strategy?: Strategy;
}

const OPPOSITE_SIDE: Record<Side, Side> = {
  top: 'bottom',
  right: 'left',
  bottom: 'top',
  left: 'right',
};

/** Inline positioning properties this module writes - cleared by clearFloating(). */
const POSITION_PROPS = ['left', 'top', 'right', 'bottom', 'position'] as const;

/**
 * Position `floating` relative to `reference` once. Sets inline top/left,
 * mirrors the resolved placement to `data-placement` (styling hook, same
 * contract tippy.js used) and offsets the arrow so it points at the reference.
 */
export async function positionFloating(
  reference: Element,
  floating: HTMLElement,
  options: FloatingOptions = {},
): Promise<void> {
  const {
    placement = 'top',
    offset: offsetPx = 8,
    arrowElement,
    padding = 8,
    strategy,
  } = options;

  const middleware = [offset(offsetPx), flip(), shift({ padding })];
  if (arrowElement) {
    middleware.push(arrow({ element: arrowElement }));
  }

  const result = await computePosition(reference, floating, {
    placement,
    strategy,
    middleware,
  });

  Object.assign(floating.style, {
    left: `${result.x}px`,
    top: `${result.y}px`,
    // Neutralize CSS-side offsets for the opposite edges: a stylesheet rule
    // (e.g. .dropdown--align-right { right: 0 }) would otherwise over-constrain
    // the box and stretch it between both edges.
    right: 'auto',
    bottom: 'auto',
  });
  if (strategy) floating.style.position = strategy;
  floating.setAttribute('data-placement', result.placement);

  if (arrowElement && result.middlewareData.arrow) {
    const { x, y } = result.middlewareData.arrow;
    const side = result.placement.split('-')[0] as Side;
    const staticSide = OPPOSITE_SIDE[side];

    Object.assign(arrowElement.style, {
      left: x != null ? `${x}px` : '',
      top: y != null ? `${y}px` : '',
      right: '',
      bottom: '',
      // Pull the arrow halfway out of the box so the rotated square forms a caret.
      [staticSide]: `${-arrowElement.offsetWidth / 2}px`,
    });
  }
}

/**
 * Keep `floating` positioned relative to `reference` while visible
 * (repositions on scroll, resize and layout changes). Returns a cleanup
 * function - call it when the floating element hides.
 */
export function attachFloating(
  reference: Element,
  floating: HTMLElement,
  options: FloatingOptions = {},
): () => void {
  return autoUpdate(
    reference,
    floating,
    () => {
      void positionFloating(reference, floating, options);
    },
    {
      // Observer-based options are unavailable in jsdom (tests).
      elementResize: typeof ResizeObserver === 'function',
      layoutShift: typeof IntersectionObserver === 'function',
    },
  );
}

/**
 * Remove every inline property positionFloating() wrote, so the element falls
 * back to its stylesheet positioning. Use it when a floating element stays in
 * the DOM after hiding (dropdown menus, popovers) - `data-placement` is kept as
 * a styling hook so arrows don't flip during the hide transition.
 */
export function clearFloating(floating: HTMLElement): void {
  POSITION_PROPS.forEach(prop => floating.style.removeProperty(prop));
}
